import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GridFilterModel, GridSortModel } from "@mui/x-data-grid-pro";
import { PAGE_URL } from "../../../constants";
import { ContactDenorm, ContactOrgDenorm } from "../../../store";
import { crm } from "../../../store/cabinetApi/crm";
import { selectDenormalizedData } from "../../../store/crm/selectors";
import { transformMuiDataGridFilterSortToApiModel } from "../../../utils/dataUtils";
import { AppCrmContactsListApiArg } from "../../../store/cabinetApi/generated/crm";
import { useDeepCompareEffect } from "react-use";
import { useCabNavigate } from "../../../utils/hooks";
import { useCalendarsConnected, CalendarConnectionStatus } from "../../../store/hooks";
import { Typography, Link } from "@mui/material";


type ContactsApiFilter = Omit<AppCrmContactsListApiArg, 'offset'|'limit'>;

const useContactsList = ({ pageSize, defaultFilter = {items: []}, defaultSort, search, includeHidden = false }: {
  pageSize: number; defaultFilter?: GridFilterModel; defaultSort?: GridSortModel; search?: string;
  includeHidden?: boolean;
}) => {
  const [contactPage, setContactPage] = useState(0);
  const [contactsFilter, setContactsFilter] = useState<GridFilterModel>(defaultFilter || { items: [] });
  const [contactsSort, setContactsSort] = useState<GridSortModel>(
    defaultSort || [{ field: 'created_at', sort: 'desc' }]
  );

  // Get calendar connection status
  const calendarStatus = useCalendarsConnected();

  const contactsApiFilter: ContactsApiFilter = useMemo(() => {
    return transformMuiDataGridFilterSortToApiModel({ filterModel: contactsFilter, sortModel: contactsSort }, true);
  }, [contactsFilter, contactsSort]);

  const { data: contactData, isFetching: loadingContacts, refetch: refetchContacts } = crm.useAppCrmContactsListQuery({
    limit: pageSize, offset: contactPage * pageSize, search,
    ...(!includeHidden && { hiddenDateIsnull: true }),
    ...contactsApiFilter,
  });

  const { data: orgData, isFetching: loadingOrgs } = crm.useAppCrmCompaniesListQuery({
    limit: 1000, hiddenDateIsnull: true
  });
  // TODO: remove limit from this request. Only adding it to get the right response shape for now
  const { data: contactTags } = crm.useAppCrmContactTagsListQuery({ limit: 1000 });

  const contacts = contactData && selectDenormalizedData(contactData) as ContactDenorm[];
  const orgs = orgData && selectDenormalizedData(orgData) as ContactOrgDenorm[];

  const [updateContact] = crm.useAppCrmContactsPartialUpdateMutation();

  const navigate = useCabNavigate();

  const handleNavigateToContact = (contactId: ContactDenorm['id']) => (
    navigate(`${PAGE_URL.CRM_PEOPLE}/${contactId}`)
  );

  const handleSetContactsFilter = (filter: GridFilterModel | undefined) => {
    setContactPage(0);
    setContactsFilter(filter || { items: [] });
  };

  const handleSetContactsSort = (sort: GridSortModel | undefined) => {
    setContactPage(0);
    setContactsSort(sort || []);
  };

  const handleUpdateContact = useCallback(async (
    contact: Partial<Omit<ContactDenorm, 'company'>> & { id: ContactDenorm['id'] }
  ) => {
    const res = await updateContact({ id: contact.id, patchedContact: {
      tags: contact.tags,
      hidden_date: contact.hidden_date,
    } });
    if ('data' in res) {
      await refetchContacts();
      return selectDenormalizedData(res.data) as ContactDenorm;
    }
  }, [refetchContacts, updateContact]);

  // Determine appropriate empty message based on calendar connection status
  const emptyMessage = useMemo(() => {
    switch (calendarStatus) {
      case CalendarConnectionStatus.NO_CALENDAR_GRANT:
        return (
          <Typography>
            <Link
              href={PAGE_URL.INTEGRATION_SETTINGS}
              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              Connect your calendar account 
            </Link>
            {" "}to start syncing contacts.
          </Typography>
        );

      case CalendarConnectionStatus.NO_LEADER_ASSOCIATIONS:
        return (
          <Typography>
            <Link
              href={PAGE_URL.MANAGE_CALENDARS}
              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              Attach calendars to your teammates
            </Link>
            {" "}to start syncing contacts.
          </Typography>
        );

      case CalendarConnectionStatus.CONNECTED:
      default:
        return "No contacts to show (if you recently connected calendars we may still be processing your contacts)";
    }
  }, [calendarStatus]);

  useDeepCompareEffect(() => setContactsFilter(defaultFilter || { items: [] }), [defaultFilter]);
  // reset filter anytime search changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setContactsFilter(defaultFilter || { items: [] }), [search]);

  return {
    contacts: contacts || [],
    rowCount: contactData?.meta?.count || 0,
    loading: loadingContacts || loadingOrgs,
    filter: contactsFilter,
    sort: contactsSort,
    orgOptions: orgs || [],
    tags: contactTags?.data || [],
    page: contactPage,
    pageSize,
    emptyMessage,
    onNavigateToContact: handleNavigateToContact,
    onSetFilter: handleSetContactsFilter,
    onSetSort: handleSetContactsSort,
    onPageChange: setContactPage,
    onUpdateContact: handleUpdateContact,
  };
};

export default useContactsList;
